import React, { useState, useEffect } from "react"
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Tooltip from "../../Tooltip"
import { url, filename } from "../url"
import { useFieldEditorController } from "../controllers/field-file-editor-controller"
import { useFieldDragController } from "../controllers/field-drag-controller"
import VideoPreview from "aldoo-ra/CMS/preview/video-preview"
import VideoThumbnail from "aldoo-ra/CMS/preview/video-thumbnail"
import MediaLibrary from "aldoo-ra/MediaLibrary"
import Input from "aldoo-ra/Input"

// Helper function to get YouTube video ID from URL
const getYouTubeVideoID = (url) => {
  const regExp =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/
  const match = url?.match(regExp)
  return match ? match[1] : null
}

export default function VideoEditor({
  onChange,
  value,
  field,
  showInfo = true,
  readOnly = false,
  parentObject,
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [currentPreviewVideo, setCurrentPreviewVideo] = useState(null)
  const [currentPreviewFileName, setCurrentPreviewFileName] = useState(null)
  const [showYouTubeModal, setShowYouTubeModal] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState("")
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null) // Track selected video index for replacement

  const {
    localValue,
    setLocalValue,
    handleChange,
    onRemoveItem,
    errors,
    setErrors,
  } = useFieldEditorController({
    value,
    field,
    onChange,
    category: "videos",
  })

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Drag controller
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, setLocalValue)

  // Close preview when 'Escape' key is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isPreviewOpen) {
        closePreview()
      }
    }

    document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isPreviewOpen])

  const openPreview = (video, fileName, index = null) => {
    setCurrentPreviewVideo(video)
    setCurrentPreviewFileName(fileName)

    if (index !== null && index !== undefined) {
      setSelectedVideoIndex(index)
    }

    setIsPreviewOpen(true)
  }

  const closePreview = () => {
    setIsPreviewOpen(false)
    setCurrentPreviewVideo(null)
    setCurrentPreviewFileName(null)
    // Don't reset selectedVideoIndex here, as we might need it for replacement
  }

  // Open Media Library - if index is provided, we're replacing that specific video
  const handleMediaLibraryOpen = (index = null) => {
    if (index !== null && index !== undefined) {
      setSelectedVideoIndex(index)
    }

    setIsMediaLibraryOpen(true)
  }

  // Handle file selection from Media Library
  const handleFileSelect = (file) => {
    if (!file) return

    // Get the file path from either file.path or file.url, whichever is available
    const filePath = file.path || (file.url ? file.url : null)

    if (!filePath) return

    const newFile = { url: filePath }

    // Case 1: Single file field (always replace)
    if (!field.multiple) {
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 2: Multiple files, but localValue is a single object (replace it)
    else if (
      selectedVideoIndex === 0 &&
      !Array.isArray(localValue) &&
      localValue &&
      localValue.url
    ) {
      // Just replace the single object
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 3: Multiple files, replacing a specific video in an array
    else if (
      selectedVideoIndex !== null &&
      Array.isArray(localValue) &&
      localValue[selectedVideoIndex]
    ) {
      // Create a deep copy to ensure React detects the change
      const updatedFiles = JSON.parse(JSON.stringify(localValue))
      updatedFiles[selectedVideoIndex] = newFile

      // Force the update by creating new references
      // Applied immediately: a deferred write replays a stale closure.
      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }
    // Case 4: Multiple files, adding a new video (no index selected)
    else {
      let updatedFiles = []

      if (Array.isArray(localValue)) {
        updatedFiles = [...localValue, newFile]
      } else if (localValue && localValue.url) {
        updatedFiles = [localValue, newFile]
      } else {
        updatedFiles = [newFile]
      }

      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }

    // Reset selected index and close media library
    setSelectedVideoIndex(null)
    setIsMediaLibraryOpen(false)
  }

  // Media library video selection handler
  const onMediaVideoSelect = (video) => {
    // Check if we're getting the video with a selectedIndex from VideoPreview
    const indexFromPreview =
      video.selectedIndex !== undefined ? video.selectedIndex : null
    const isFromPreview = video.fromPreview === true

    if (!video || (!video.url && !video.path)) {
      return
    }

    const filePath = video.path
    const newFile = { url: filePath }

    // Use the index from preview if available, otherwise use the local selectedVideoIndex
    const indexToUse =
      indexFromPreview !== null ? indexFromPreview : selectedVideoIndex

    // Case 1: Multiple field, but localValue is a single object (replace it)
    if (
      indexToUse === 0 &&
      field.multiple &&
      !Array.isArray(localValue) &&
      localValue &&
      localValue.url
    ) {
      // Just replace the single object
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 2: We're replacing a specific video in an array
    else if (
      indexToUse !== null &&
      Array.isArray(localValue) &&
      localValue[indexToUse]
    ) {
      // We're replacing an existing video
      const updatedFiles = [...localValue]
      updatedFiles[indexToUse] = newFile

      // Update the state
      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }
    // Case 3: Single field (always replace)
    else if (!field.multiple) {
      // Single video field
      setLocalValue(newFile)
      onChange(newFile)
    }
    // Case 4: Multiple field, adding new
    else {
      // Multiple video field - adding new
      let updatedFiles = []

      if (Array.isArray(localValue)) {
        updatedFiles = [...localValue, newFile]
      } else if (localValue && localValue.url) {
        updatedFiles = [localValue, newFile]
      } else {
        updatedFiles = [newFile]
      }

      setLocalValue(updatedFiles)
      onChange(updatedFiles)
    }

    // Update the preview with the new video
    if (isFromPreview) {
      // If it's from the preview, update the preview video but don't close it
      setCurrentPreviewVideo(url(filePath))
      setCurrentPreviewFileName(filename(filePath))
    } else {
      // Only close if not coming from preview
      closePreview()
    }
  }

  const addYouTubeUrl = () => {
    if (!youtubeUrl.trim()) return

    const videoId = getYouTubeVideoID(youtubeUrl.trim())
    if (!videoId) {
      setErrors(["Invalid YouTube URL"])
      return
    }

    const newVideo = {
      url: youtubeUrl.trim(),
      file: { name: youtubeUrl.trim() },
    }

    if (field.multiple) {
      const original = localValue || []
      setLocalValue([...original, newVideo])
    } else {
      setLocalValue(newVideo)
    }

    setYoutubeUrl("")
    setShowYouTubeModal(false)
  }

  const renderItem = (item, index) => {
    if (!item) return null
    if (item.url && item.url.includes("blob:")) return null
    return (
      <Tooltip key={index} content={filename(url(item.url))} position="top">
        <div
          className="relative flex-none w-32 h-32 border border-admin_bordered rounded-md overflow-hidden group"
          draggable={!readOnly && localValue?.length > 1}
          onDragStart={!readOnly && localValue?.length > 1 ? () => handleDragStart(index) : undefined}
          onDragOver={!readOnly && localValue?.length > 1 ? (e) => {
            e.preventDefault()
            handleDragOver(index)
          } : undefined}
          onDragEnd={!readOnly && localValue?.length > 1 ? handleDragEnd : undefined}
        >
          <VideoThumbnail
            videoUrl={url(item.url)}
            className="cursor-pointer"
            width={128}
            height={128}
            onClick={() =>
              openPreview(url(item.url), filename(item.url), index)
            }
          />

          {/* Upload progress */}
          {item.progress > 0 && item.progress < 100 && (
            <div
              className="absolute top-0 left-0 right-0 bg-admin_primary"
              style={{ width: `${item.progress}%` }}
            >
              <div className="ml-1 w-full text-xs text-white">
                {item.progress}%
              </div>
            </div>
          )}

          {!readOnly && (
            <>
              <div className="absolute top-1 left-1 bg-admin_neutral_2 text-admin_text_inv rounded-full p-1 cursor-move opacity-0 group-hover:opacity-100 transition-opacity">
                <Bars3Icon className="w-4 h-4" />
              </div>

              <div className="absolute top-1 right-1 flex space-x-1">
                {/* Replace button */}
                <button
                  className="text-white bg-admin_primary rounded-full w-6 h-6 flex items-center justify-center hover:bg-admin_primary_dk focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleMediaLibraryOpen(index)
                  }}
                  title="Replace video"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {/* Delete button */}
                <button
                  className="text-white bg-admin_error rounded-full w-6 h-6 flex items-center justify-center hover:bg-admin_error/90 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onRemoveItem(index)
                  }}
                  title="Remove video"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
          <span className="mt-2 text-center text-xs text-admin_text block">
            {filename(item.url)}
          </span>
        </div>
      </Tooltip>
    )
  }

  return (
    <div className="video-editor">
      <div className="flex flex-row justify-end items-center">
        {/* Media Library and YouTube URL buttons */}
        {!readOnly && (
          <div className="flex flex-row items-center space-x-4">
            <Button
              variant="admin_secondary"
              className="mb-4"
              onClick={() => handleMediaLibraryOpen(null)}
            >
              {field.multiple ? "Add Video" : "Select Video"}
            </Button>

            <Button
              variant="admin_secondary"
              className="mb-4"
              onClick={() => setShowYouTubeModal(true)}
            >
              Add YouTube
            </Button>
          </div>
        )}
      </div>

      {!readOnly && field.multiple && localValue?.length > 0 && (
        <div className="text-sm text-admin_text mb-2 flex flex-row items-center">
          <Button
            variant="admin_secondary"
            className="mr-5"
            onClick={() => {
              setLocalValue(field.multiple ? [] : null)
            }}
          >
            {`${field.multiple ? "Clear All" : "Clear"}`}
          </Button>
          {field.multiple && (
            <div className="text-sm text-admin_text">
              {localValue.length} {localValue.length === 1 ? "video" : "videos"}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-4 overflow-x-auto py-4">
        {/* Render video items */}
        {Array.isArray(localValue)
          ? localValue?.map((item, index) => renderItem(item, index))
          : renderItem(localValue, 0)}
      </div>

      {errors.length > 0 && (
        <div className="text-error text-center w-full mb-4">
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </div>
      )}

      {/* Full screen video preview with media library support */}
      {isPreviewOpen && (
        <VideoPreview
          isOpen={isPreviewOpen}
          videoUrl={currentPreviewVideo}
          fileName={currentPreviewFileName}
          onClose={closePreview}
          onVideoSelect={onMediaVideoSelect}
          selectedVideoIndex={selectedVideoIndex} // Pass the index to VideoPreview
        />
      )}

      {/* YouTube URL Modal */}
      {showYouTubeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-admin_paper p-6 rounded-lg shadow-lg relative w-[500px]">
            <button
              className="absolute top-2 right-2 text-admin_text_secondary hover:text-admin_error"
              onClick={() => setShowYouTubeModal(false)}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            <h3 className="text-lg font-semibold text-admin_text mb-4">
              Add YouTube Video URL
            </h3>

            <Input
              variant="admin"
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="text-admin_text bg-admin_paper w-full px-4 py-2 border border-admin_bordered rounded-md focus:ring-2 focus:ring-primary focus:outline-none"
              placeholder="Enter YouTube URL"
            />
            
            <div className="flex justify-end mt-4">
              <Button className="w-32" variant="admin_primary" onClick={addYouTubeUrl}>
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Media Library Modal */}
      <MediaLibrary
        isOpen={isMediaLibraryOpen}
        onClose={() => {
          setIsMediaLibraryOpen(false)
          setSelectedVideoIndex(null) // Reset index when closing without selection
        }}
        onFileSelect={handleFileSelect}
        title={
          selectedVideoIndex !== null
            ? "Replace Video"
            : field.multiple
            ? "Add Video"
            : "Select Video"
        }
        defaultFileType="video" // Set the default file type to videos
      />
    </div>
  )
}
